import { loadMobileCampaignOptions } from '../../utils/analytics/mobileCampaigns';
import React, { useEffect, useRef, useState } from 'react';

import Accordion from '../accordion';
import CustomButton from '../customButton';
import MultiSelector from '../multiSelector';
import ActionButtons from '../actionButtons';

import utils from '../../utils';

import './styles.scss';
import { useSelector } from 'react-redux';
import { getSelectedProject } from '../../redux/projects/actions';
import { loadCmsFilterOptions } from '../../utils/analytics/cmsFilters';
import { getIntegrations } from '../../redux/integration/actions';
import { getAnalyticsTemplates } from '../../redux/templates/actions';
import { isRetailMediaTemplate } from '../../analyticsWidgets/shared';

const AnalyticsSelector = ({
  sites,
  selectedDashboard,
  devices,
  assets,
  appliedFilters,
  onSelection,
  cmsIntegration,
  customLabel
}) => {
  const [medias, setMedias] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [isLoadingMedias, setIsLoadingMedias] = useState(false);
  const [isLoadingAdvertisers, setIsLoadingAdvertisers] = useState(false);

  const [localFilters, setLocalFilters] = useState({
    // availableDevices: full list of devices displayed in the selector
    // selectedDevices: subset of devices currently selected by the user
    availableDevices: devices,
    selectedDevices: devices,
    selectedSites: sites,
    selectedAssets: assets,
    sites: sites.map(site => site.id),
    selectedAdvertisers: [],
    selectedMedias: []
  });

  const [siteQuickView, setsiteQuickView] = useState('');
  const [cmsFilterError, setCmsFilterError] = useState('');
  const [campaignsLoadedKey, setCampaignsLoadedKey] = useState(null);
  const onSelectionRef = useRef(onSelection);
  onSelectionRef.current = onSelection;

  const project = useSelector(getSelectedProject);
  const integrations = useSelector(getIntegrations);
  const templates = useSelector(getAnalyticsTemplates);

  const getAssetsBySelectedSites = (availableAssets = [], selectedSites = []) => {
    const selectedSiteIds = new Set((selectedSites || []).map(site => site.id));

    if (selectedSiteIds.size === 0) {
      return [];
    }

    return (availableAssets || []).filter(asset => {
      const assetSiteId = asset.siteId || asset?.data?.siteId;
      return selectedSiteIds.has(assetSiteId);
    });
  };

  const getDevicesByAssets = (selectedAssets = []) => {
    const devicesById = new Map();
    selectedAssets.forEach(asset => {
      (asset.devices || []).forEach(device => devicesById.set(device.id, device));
    });
    return Array.from(devicesById.values());
  };

  useEffect(() => {
    // keep localFilters in sync when parent devices/sites props change (page load or project change)
    setLocalFilters(prev => {
      const syncedSites = sites || prev.selectedSites || [];
      const filteredAssets = getAssetsBySelectedSites(assets || [], syncedSites);
      const selectedAssets = (prev.selectedAssets || []).filter(selectedAsset => {
        return filteredAssets.some(asset => asset.id === selectedAsset.id);
      });

      return {
        ...prev,
        availableDevices: devices || [],
        // if there was no previous explicit selection, default selected to all available
        selectedDevices: prev.selectedDevices && prev.selectedDevices.length > 0 ? prev.selectedDevices : devices || [],
        selectedSites: syncedSites,
        sites: syncedSites.map(site => site.id),
        selectedAssets
      };
    });

  }, [devices, sites, assets, selectedDashboard]);

  const vendorIds = (Array.isArray(cmsIntegration) ? cmsIntegration : [cmsIntegration])
    .filter(Boolean).map(vendor => vendor.toLowerCase());
  const activeCms = (integrations || []).filter(integration => vendorIds.includes(integration.vendor?.toLowerCase()));
  const mobile = selectedDashboard?.headers === 'mobile' || isRetailMediaTemplate(selectedDashboard) ||
    selectedDashboard?.compatibleDatasources?.includes('MOBILE_M');
  const cmsKey = JSON.stringify({
    projectId: project?.id,
    companyId: project?.companyId,
    availableSites: sites,
    sources: mobile ? vendorIds.map(vendor => ({ vendor })) : activeCms,
    startDate: appliedFilters.startDate,
    endDate: appliedFilters.endDate,
    ...(mobile ? {
      mobile: true,
      templates,
      selectedSites: appliedFilters.selectedSites || [],
      selectedAssets: appliedFilters.selectedAssets,
      timezone: appliedFilters.timezone,
      filterByWeekday: appliedFilters.filterByWeekday,
      filterByTime: appliedFilters.filterByTime
    } : { devices: appliedFilters.selectedDevices || [] })
  });
  const campaignKey = JSON.stringify(appliedFilters.selectedAdvertisers || []);
  const hasCms = vendorIds.length > 0;

  useEffect(() => {
    let active = true;
    const context = JSON.parse(cmsKey);
    setAdvertisers([]);
    setCmsFilterError('');
    if (context.mobile) onSelectionRef.current({ campaignsReady: false });
    setIsLoadingAdvertisers(Boolean(context.mobile ? context.companyId : context.sources.length));
    const load = context.mobile && !context.sources.length
      ? loadMobileCampaignOptions(context.companyId, context.availableSites)
      : context.projectId && context.sources.length && context.startDate && context.endDate
        ? loadCmsFilterOptions('advertisers', context) : Promise.resolve([]);
    load.then(options => {
      if (!active) return;
      setAdvertisers(options);
      setCampaignsLoadedKey(cmsKey);
      const previousIds = new Set((JSON.parse(campaignKey) || []).map(item => item.id));
      const retained = context.mobile ? options.filter(item => previousIds.has(item.id)) : [];
      const selected = retained.length ? retained : options;
      setLocalFilters(previous => ({ ...previous, selectedAdvertisers: selected }));
      onSelectionRef.current({ selectedAdvertisers: selected, ...(context.mobile ? { campaignsReady: true } : {}) });
    }).catch(() => {
      if (active) setCmsFilterError('Unable to load campaigns. Please try again.');
    }).finally(() => { if (active) setIsLoadingAdvertisers(false); });
    return () => { active = false; };
  }, [cmsKey]);

  useEffect(() => {
    let active = true;
    const context = JSON.parse(cmsKey);
    setMedias([]);
    if (campaignsLoadedKey !== cmsKey) return () => { active = false; };
    setIsLoadingMedias(context.sources.length > 0);
    const load = context.projectId && context.sources.length && context.startDate && context.endDate
      ? loadCmsFilterOptions('medias', context, JSON.parse(campaignKey)) : Promise.resolve([]);
    load.then(options => {
      if (!active) return;
      setMedias(options);
      setLocalFilters(previous => ({ ...previous, selectedMedias: options }));
      onSelectionRef.current({ selectedMedias: options });
    }).catch(() => {
      if (active) setCmsFilterError('Unable to load CMS media. Please try again.');
    }).finally(() => { if (active) setIsLoadingMedias(false); });
    return () => { active = false; };
  }, [cmsKey, campaignKey, campaignsLoadedKey]);

  const isButtonDisabled = (previousItems = [], currentItems = []) => {
    return currentItems.length === 0 || utils.compareArrays(previousItems, currentItems);
  };

  const onQuickView = item => {
    if (siteQuickView === item.id) {
      // restore previous selection locally (do not notify parent until Update Analytics is clicked)
      setsiteQuickView('');
    } else {
      // Set available devices from the Assets attached to the quick-view Site.
      const relatedAssets = getAssetsBySelectedSites(assets, [item]);
      const relatedDevices = getDevicesByAssets(relatedAssets);
      setLocalFilters({
        ...localFilters,
        availableDevices: relatedDevices,
        selectedDevices: relatedDevices,
        selectedSites: [item],
        sites: [item.id],
        // Auto-select assets related to the selected site
        selectedAssets: relatedAssets
      });
      setsiteQuickView(item.id);
    }
  };

  const populateDevices = selectedSites => {
    const relatedAssets = getAssetsBySelectedSites(assets, selectedSites);
    const localDevices = getDevicesByAssets(relatedAssets);
    // When sites change, auto-select related assets and keep everything in sync
    const newLocalFilters = {
      ...localFilters,
      availableDevices: localDevices,
      selectedDevices: localDevices,
      selectedSites: selectedSites,
      sites: selectedSites.map(site => site.id),
      selectedAssets: relatedAssets
    };
    setLocalFilters(newLocalFilters);
    // Do NOT notify parent here; parent will be updated when the user clicks "Update Analytics"
  };

  const filteredAssets = getAssetsBySelectedSites(assets, localFilters?.selectedSites);

  const renderActionButtons = device => {
    const styles = `action-buttons ${siteQuickView === device.id ? 'is-active' : ''}`;
    return (
      <>
        <div className={styles}>
          <ActionButtons
            buttons={[
              {
                id: `quick-data-${device.id}`,
                icon: siteQuickView === device.id ? 'soloDark' : 'eyeDark',
                key: 'quick-data',
                tooltip: 'See the data',
                onPress: () => onQuickView(device)
              }
            ]}
          />
        </div>
      </>
    );
  };

  let sections = [
    {
      header: `${customLabel || 'Sites'} (${
        appliedFilters?.selectedSites?.length > 0 ? appliedFilters?.selectedSites?.length : sites?.length
      }/${sites?.length})`,
      body: (
        <>
          <MultiSelector
            items={sites}
            onSelect={value => {
              populateDevices(value);
            }}
            displayField="displayName"
            emptyMessage={`No ${customLabel || 'Sites'} found`}
            customSearchPlaceHolder={`Search ${customLabel || 'Sites'}`}
            sortField="displayName"
            renderActionButtons={renderActionButtons}
          />
          {sites?.length > 0 && (
            <CustomButton
              handleClick={() => {
                onSelection(localFilters);
                setsiteQuickView('');
              }}
              classes="btn-primary btn-slim"
              title="Update Analytics"
              disabled={isButtonDisabled(appliedFilters?.selectedSites, localFilters?.selectedSites)}
            />
          )}
        </>
      )
    },
    {
      header: `Assets(${localFilters?.selectedAssets?.length}/${filteredAssets?.length})`,
      body: (
        <>
          <MultiSelector
            items={filteredAssets}
            onSelect={value => {
              setLocalFilters({ ...localFilters, selectedAssets: value });
            }}
            displayField={'displayName' || 'id'}
            emptyMessage={`No Assets found for selected ${customLabel || 'Sites'}`}
            customSearchPlaceHolder={`Search Assets`}
            sortField="displayName"
          />
          {filteredAssets?.length > 0 && (
            <CustomButton
              handleClick={() =>
                onSelection({
                  selectedAssets: localFilters?.selectedAssets || []
                })
              }
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedAssets, localFilters?.selectedAssets)}
            />
          )}
        </>
      )
    },
    {
      header: `Devices (${localFilters?.selectedDevices?.length}/${localFilters?.availableDevices?.length})`,
      body: (
        <>
          <MultiSelector
            // show the available devices for the currently selected sites
            items={localFilters?.availableDevices}
            // when the user toggles selection, only update the selectedDevices set
            onSelect={value => {
              setLocalFilters({ ...localFilters, selectedDevices: value });
            }}
            displayField="displayName"
            emptyMessage="No Devices found for selected Sites"
            customSearchPlaceHolder="Search Device"
            sortField="displayName"
          />
          {appliedFilters?.selectedDevices?.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedDevices, localFilters?.selectedDevices)}
            />
          )}
        </>
      )
    },
    {
      header: isLoadingAdvertisers
        ? 'Loading Advertisers...'
        : `Campaigns/Advertisers (${appliedFilters?.selectedAdvertisers?.length}/${advertisers?.length})`,
      body: (
        <>
          <MultiSelector
            items={advertisers}
            hasCustomSelectedItems={true}
            customSelectedItems={localFilters.selectedAdvertisers}
            onSelect={value => {
              setLocalFilters(previous => ({ ...previous, selectedAdvertisers: value }));
            }}
            displayField="displayName"
            emptyMessage={mobile ? "No campaigns assigned to these sites" : "No Campaigns found for this period"}
            customSearchPlaceHolder="Search Campaign"
            sortField="name"
          />
          {advertisers.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedAdvertisers, localFilters?.selectedAdvertisers)}
            />
          )}
        </>
      )
    },
    {
      header: isLoadingMedias
        ? 'Loading Medias...'
        : `Media (${appliedFilters?.selectedMedias?.length}/${medias?.length})`,
      body: (
        <>
          <MultiSelector
            items={medias}
            hasCustomSelectedItems={true}
            customSelectedItems={localFilters.selectedMedias}
            onSelect={value => {
              setLocalFilters(previous => ({ ...previous, selectedMedias: value }));
            }}
            displayField="displayName"
            emptyMessage="No Medias found for this period"
            customSearchPlaceHolder="Search Media"
            sortField="name"
          />
          {medias.length > 0 && (
            <CustomButton
              handleClick={() => onSelection(localFilters)}
              classes="btn-primary btn-slim"
              title="Apply"
              disabled={isButtonDisabled(appliedFilters?.selectedMedias, localFilters?.selectedMedias)}
            />
          )}
        </>
      )
    }
  ];

  if (!hasCms) {
    // Mobile campaigns use Core entity IDs even without a CMS integration.
    sections = sections.slice(0, sections.length - (mobile ? 1 : 2));
  }

  return (
    <div className={`side-menu ${hasCms ? 'cms-mode' : ''}`}>
      {cmsFilterError && <p role="alert">{cmsFilterError}</p>}
      <Accordion items={sections}></Accordion>
    </div>
  );
};

export default AnalyticsSelector;
